import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useAlert, useApi } from '@app/hooks';

import { OutletDistributorForm, OutletDistributorRow } from '../types';

type UseOutletDistributorsStateOptions = {
  outlet_id?: number;
  distributor_ids?: number[];
  deferChanges?: boolean;
};

const mapFormToRow = (
  item: OutletDistributorForm,
  distributorNames: Map<number, string>
): OutletDistributorRow => {
  const distributorId = Number(item.distributor?.id ?? item.distributor_id);

  return {
    distributor_id: distributorId,
    distributor_name: item.distributor?.name || distributorNames.get(distributorId) || '',
    distributor_outlet_id: item.distributor_outlet_id,
    sfa_id: item.sfa_id,
    is_general: item.is_general,
    is_active: item.is_active,
  };
};

const mapRowToForm = (row: OutletDistributorRow): OutletDistributorForm => ({
  distributor_id: row.distributor_id,
  distributor: {
    id: row.distributor_id,
    name: row.distributor_name,
  },
  distributor_outlet_id: row.distributor_outlet_id,
  sfa_id: row.sfa_id,
  is_general: row.is_general,
  is_active: row.is_active,
});

export const useOutletDistributorsState = ({
  outlet_id,
  distributor_ids = [],
  deferChanges = false,
}: UseOutletDistributorsStateOptions) => {
  const isLocalMode = !outlet_id;
  const isDeferredMode = deferChanges && !!outlet_id;
  const usePendingState = isLocalMode || isDeferredMode;

  const [distributors, setDistributors] = useState<OutletDistributorRow[]>([]);
  const [localItems, setLocalItems] = useState<OutletDistributorForm[]>([]);
  const [allDistributors, setAllDistributors] = useState<EntityBase[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isTouched, setIsTouched] = useState(false);
  const initialItemsRef = useRef<OutletDistributorRow[]>([]);
  const api = useApi();
  const { error } = useAlert();

  const distributorNames = useMemo(
    () => new Map<number, string>(
      allDistributors.map((item) => [Number(item.id), item.name])
    ),
    [allDistributors]
  );
  const distributorNamesRef = useRef(distributorNames);
  distributorNamesRef.current = distributorNames;

  useEffect(() => {
    const loadAllDistributors = async () => {
      const response = await api.distributors.getDistributors({
        limit: 100000,
        offset: 0,
        sort: '+id',
        filter: {},
      });

      setAllDistributors(
        (response?.data?.data || []).map((item) => ({
          id: item.id,
          name: item.name || item.legal_name || '',
        }))
      );
    };

    loadAllDistributors();
  }, []);

  useEffect(() => {
    if (!isLocalMode || !distributor_ids.length) {
      return;
    }

    setLocalItems((items) => {
      const existingIds = new Set(
        items.map((item) => Number(item.distributor?.id ?? item.distributor_id))
      );

      const nextItems = distributor_ids
        .filter((id) => !existingIds.has(id))
        .map((id, index) => ({
          distributor_id: id,
          distributor: allDistributors.find((item) => Number(item.id) === id) || { id, name: '' },
          distributor_outlet_id: '',
          sfa_id: null,
          is_general: index === 0,
          is_active: true,
        }));

      if (!nextItems.length) {
        return items;
      }

      return [...items, ...nextItems];
    });
  }, [allDistributors, distributor_ids, isLocalMode]);

  useEffect(() => {
    if (!usePendingState) {
      return;
    }

    setDistributors(localItems.map((item) => mapFormToRow(item, distributorNames)));
  }, [distributorNames, localItems, usePendingState]);

  const mapApiRows = useCallback((items: Array<{
    distributor_id: number;
    distributor_outlet_id: string;
    sfa_id: number;
    is_general: boolean;
    is_active: boolean;
  }>) => (
    items.map((item) => ({
      distributor_id: item.distributor_id,
      distributor_name: distributorNamesRef.current.get(item.distributor_id) || '',
      distributor_outlet_id: item.distributor_outlet_id,
      sfa_id: item.sfa_id,
      is_general: item.is_general,
      is_active: item.is_active,
    }))
  ), []);

  const loadOutletDistributors = useCallback(async () => {
    if (!outlet_id) {
      return [];
    }

    setIsLoading(true);

    const outletDistributorsResponse = await api.acb.getOutletDistributors(outlet_id, {
      limit: 1000,
      offset: 0,
      sort: '+distributor_id',
      filter: {},
    });

    const rows = mapApiRows(outletDistributorsResponse?.data?.data || []);

    if (isDeferredMode) {
      initialItemsRef.current = rows;
      setLocalItems(rows.map(mapRowToForm));
      setDistributors(rows);
    } else if (!isLocalMode) {
      setDistributors(rows);
    }

    setIsLoading(false);
    return rows;
  }, [isDeferredMode, isLocalMode, mapApiRows, outlet_id]);

  useEffect(() => {
    if (isLocalMode) {
      return;
    }

    loadOutletDistributors();
  }, [isLocalMode, loadOutletDistributors]);

  useEffect(() => {
    if (usePendingState || !distributorNames.size) {
      return;
    }

    setDistributors((items) => items.map((item) => ({
      ...item,
      distributor_name: distributorNames.get(item.distributor_id) || item.distributor_name,
    })));
  }, [distributorNames, usePendingState]);

  const ensureSfaIdUnique = useCallback(async (
    sfaId?: number | string | null,
    ignoreDistributorId?: number
  ) => {
    if (!sfaId) {
      return true;
    }

    if (isLocalMode) {
      const hasLocalDuplicate = localItems.some((item) => (
        Number(item.distributor?.id ?? item.distributor_id) !== ignoreDistributorId
        && Number(item.sfa_id) === Number(sfaId)
      ));

      if (hasLocalDuplicate) {
        error('Этот SFA ID уже указан у другого дистрибьютора');
        return false;
      }
    }

    const response = await api.acb.getMasterOutlets({
      limit: 100,
      offset: 0,
      sort: '+id',
      filter: { sfa_id: String(sfaId) },
    });

    const hasConflict = (response?.data?.data || []).some(
      (item) => item.id !== outlet_id
    );

    if (hasConflict) {
      error('ТТ с таким SFA ID в системе уже есть');
      return false;
    }

    return true;
  }, [api, error, isLocalMode, localItems, outlet_id]);

  const addOutletDistributor = useCallback(async (payload: OutletDistributorForm) => {
    if (!(await ensureSfaIdUnique(payload.sfa_id))) {
      return false;
    }

    if (usePendingState) {
      setLocalItems((items) => [...items, payload]);
      setIsTouched(true);
      return true;
    }

    if (!outlet_id) {
      return false;
    }

    const distributorId = Number(payload.distributor?.id ?? payload.distributor_id);
    const response = await api.acb.createOutletDistributor(outlet_id, {
      distributor_id: distributorId,
      is_general: payload.is_general,
      distributor_outlet_id: payload.distributor_outlet_id,
      sfa_id: payload.sfa_id ? Number(payload.sfa_id) : null,
    });

    if (response?.code === 201) {
      await loadOutletDistributors();
      return true;
    }

    return false;
  }, [ensureSfaIdUnique, loadOutletDistributors, outlet_id, usePendingState]);

  const updateOutletDistributor = useCallback(async (
    distributorId: number,
    payload: OutletDistributorForm
  ) => {
    if (!(await ensureSfaIdUnique(payload.sfa_id, distributorId))) {
      return false;
    }

    if (usePendingState) {
      setLocalItems((items) => items.map((item) => (
        Number(item.distributor?.id ?? item.distributor_id) === distributorId
          ? payload
          : item
      )));
      setIsTouched(true);
      return true;
    }

    if (!outlet_id) {
      return false;
    }

    const response = await api.acb.updateOutletDistributor(outlet_id, distributorId, {
      is_general: payload.is_general,
      distributor_outlet_id: payload.distributor_outlet_id,
      sfa_id: payload.sfa_id ? Number(payload.sfa_id) : null,
      is_active: payload.is_active,
    });

    if (response?.code === 200) {
      await loadOutletDistributors();
      return true;
    }

    return false;
  }, [ensureSfaIdUnique, loadOutletDistributors, outlet_id, usePendingState]);

  const applyDistributorChanges = useCallback(async (
    nextDistributors: Array<{ id: number; is_general: boolean }>,
    selectedIds: number[]
  ) => {
    if (!outlet_id || !selectedIds.length) {
      return false;
    }

    const currentById = new Map(
      distributors.map((item) => [item.distributor_id, item])
    );
    const nextById = new Map(
      nextDistributors.map((item) => [item.id, item])
    );
    const selectedSet = new Set(selectedIds);

    const nextGeneralId = nextDistributors.find(
      (item) => selectedSet.has(item.id) && item.is_general
    )?.id;

    if (nextGeneralId) {
      const currentGeneral = distributors.find((item) => item.is_general);
      if (currentGeneral && currentGeneral.distributor_id !== nextGeneralId) {
        const response = await api.acb.updateOutletDistributor(
          outlet_id,
          currentGeneral.distributor_id,
          {
            is_general: false,
            distributor_outlet_id: currentGeneral.distributor_outlet_id,
            sfa_id: currentGeneral.sfa_id,
            is_active: currentGeneral.is_active,
          }
        );

        if (response?.code !== 200) {
          return false;
        }
      }
    }

    for (const distributorId of selectedIds) {
      const current = currentById.get(distributorId);
      const next = nextById.get(distributorId);

      if (next && !current) {
        const response = await api.acb.createOutletDistributor(outlet_id, {
          distributor_id: distributorId,
          is_general: next.is_general,
          distributor_outlet_id: '',
          sfa_id: 0,
        });

        if (response?.code !== 201) {
          return false;
        }
        continue;
      }

      if (current && !next) {
        const response = await api.acb.updateOutletDistributor(outlet_id, distributorId, {
          is_general: false,
          distributor_outlet_id: current.distributor_outlet_id,
          sfa_id: current.sfa_id,
          is_active: false,
        });

        if (response?.code !== 200) {
          return false;
        }
        continue;
      }

      if (current && next) {
        const response = await api.acb.updateOutletDistributor(outlet_id, distributorId, {
          is_general: next.is_general,
          distributor_outlet_id: current.distributor_outlet_id,
          sfa_id: current.sfa_id,
          is_active: current.is_active,
        });

        if (response?.code !== 200) {
          return false;
        }
      }
    }

    await loadOutletDistributors();
    return true;
  }, [distributors, loadOutletDistributors, outlet_id]);

  const saveOutletDistributors = useCallback(async (targetOutletId: number) => {
    if (!isLocalMode || !localItems.length) {
      return true;
    }

    for (const item of localItems) {
      const response = await api.acb.createOutletDistributor(targetOutletId, {
        distributor_id: Number(item.distributor?.id ?? item.distributor_id),
        is_general: item.is_general,
        distributor_outlet_id: item.distributor_outlet_id,
        sfa_id: item.sfa_id ? Number(item.sfa_id) : null,
      });

      if (response?.code !== 201) {
        return false;
      }
    }

    setIsTouched(false);
    return true;
  }, [isLocalMode, localItems]);

  const validateOutletDistributors = useCallback(async () => {
    if (!isLocalMode) {
      return true;
    }

    for (const item of localItems) {
      const distributorId = Number(item.distributor?.id ?? item.distributor_id);
      if (!(await ensureSfaIdUnique(item.sfa_id, distributorId))) {
        return false;
      }
    }

    return true;
  }, [ensureSfaIdUnique, isLocalMode, localItems]);

  return {
    distributors,
    distributorNames,
    isLoading: isLocalMode ? false : isLoading,
    isTouched,
    addOutletDistributor,
    updateOutletDistributor,
    applyDistributorChanges,
    saveOutletDistributors,
    validateOutletDistributors,
  };
};
